import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigationType } from "react-router-dom";
import { api } from "@/lib/api";

const CACHE_KEY = "feed-cache-v2";
const readCache = () => { try { return JSON.parse(sessionStorage.getItem(CACHE_KEY) || "{}"); } catch { return {}; } };
const writeCache = (k, v) => { try { const c = readCache(); c[k] = v; sessionStorage.setItem(CACHE_KEY, JSON.stringify(c)); } catch {} };

// Fetches /public/articles with cursor pagination. Loaded batches are cached per feed key and
// restored when the visitor comes back with the browser/site back button (POP navigation).
// auto=false → the first extra batch needs a tap (loadMore); afterwards the sentinel auto-loads (autoMode).
export function useInfiniteArticles({ section = null, pageSize = 12, seedItems = null, initialSkip = 0, auto = true } = {}) {
    const navType = useNavigationType();
    const feedKey = `${window.location.pathname}|${section || ""}|${initialSkip}`;
    const cachedEntry = navType === "POP" ? readCache()[feedKey] : null;
    const restored = useRef(cachedEntry?.items?.length ? cachedEntry : null);
    const init = restored.current || null;
    const [items, setItems] = useState(init?.items || seedItems || []);
    const [cursor, setCursor] = useState(init?.cursor || null);
    const [hasMore, setHasMore] = useState(init ? init.hasMore : true);
    const [autoMode, setAutoMode] = useState(init ? !!init.autoMode : false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const seenIds = useRef(new Set((init?.items || seedItems || []).map((a) => a.id)));
    const sentinelRef = useRef(null);
    const inflight = useRef(false);
    const sectionRef = useRef(section);
    const cursorRef = useRef(init?.cursor || null);
    const hasMoreRef = useRef(init ? init.hasMore : true);
    const autoRef = useRef(init ? !!init.autoMode : false);
    const firstBatchDone = useRef(!!init);
    const itemsRef = useRef(items);
    itemsRef.current = items;

    const fetchPage = useCallback(async (forSection) => {
        if (inflight.current || !hasMoreRef.current) return;
        inflight.current = true;
        const isExtraBatch = firstBatchDone.current;
        setLoading(true);
        setError(null);
        try {
            const params = { limit: pageSize };
            if (cursorRef.current) params.cursor = cursorRef.current;
            else if (initialSkip) params.skip = initialSkip;
            if (forSection) params.section = forSection;
            const r = await api.get("/public/articles", { params });
            if (sectionRef.current !== forSection) return;
            const fresh = (r.data?.items || []).filter((a) => {
                if (seenIds.current.has(a.id)) return false;
                seenIds.current.add(a.id);
                return true;
            });
            setItems((old) => old.concat(fresh));
            cursorRef.current = r.data?.next_cursor || null;
            hasMoreRef.current = !!r.data?.has_more;
            setCursor(cursorRef.current);
            setHasMore(hasMoreRef.current);
            firstBatchDone.current = true;
            if (isExtraBatch && !autoRef.current) { autoRef.current = true; setAutoMode(true); }
        } catch (e) {
            if (sectionRef.current === forSection) setError(e?.response?.status || "network");
        } finally {
            inflight.current = false;
            if (sectionRef.current === forSection) setLoading(false);
        }
    }, [pageSize, initialSkip]);

    const loadMore = useCallback(() => fetchPage(sectionRef.current), [fetchPage]);

    // (Re)initialise whenever the section changes (skipped on mount when restored from cache; StrictMode-safe).
    const initFor = useRef(restored.current ? section : undefined);
    useEffect(() => {
        if (restored.current && initFor.current === section) return;
        initFor.current = section;
        sectionRef.current = section;
        cursorRef.current = null;
        hasMoreRef.current = true;
        autoRef.current = false;
        firstBatchDone.current = !!(seedItems && seedItems.length);
        inflight.current = false;
        seenIds.current = new Set(seedItems ? seedItems.map((a) => a.id) : []);
        setItems(seedItems || []);
        setCursor(null);
        setHasMore(true);
        setAutoMode(false);
        setError(null);
        if (!seedItems || seedItems.length === 0) fetchPage(section);
        // eslint-disable-next-line
    }, [section]);

    // Persist the loaded batches so a back navigation lands on the same list.
    useEffect(() => () => { if (itemsRef.current.length) writeCache(feedKey, { items: itemsRef.current, cursor: cursorRef.current, hasMore: hasMoreRef.current, autoMode: autoRef.current }); }, [feedKey]);

    // IntersectionObserver on the sentinel — only once auto-loading is wanted (auto prop or after the first tap).
    const autoActive = auto || autoMode;
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || !autoActive || !hasMore || error) return;
        const nearEnd = () => el.getBoundingClientRect().top < window.innerHeight + 600;
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting) || nearEnd()) loadMore();
        }, { rootMargin: "600px 0px" });
        io.observe(el);
        let raf = 0;   // scroll fallback: fast jumps can skip the observer entirely
        const onScroll = () => { if (raf) return; raf = requestAnimationFrame(() => { raf = 0; if (nearEnd()) loadMore(); }); };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => { io.disconnect(); window.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
    }, [loadMore, items.length, autoActive, hasMore, error]);

    const byNewest = (a, b) => String(b.published_at || "").localeCompare(String(a.published_at || "")) || String(b.id).localeCompare(String(a.id));
    const prepend = useCallback((articles) => {
        const list = (Array.isArray(articles) ? articles : [articles]).filter((a) => a?.id && !seenIds.current.has(a.id));
        if (!list.length) return;
        for (const a of list) seenIds.current.add(a.id);
        setItems((old) => [...list.sort(byNewest), ...old]);
    }, []);
    const replace = useCallback((articles) => {
        const map = new Map((articles || []).filter((a) => a?.id).map((a) => [a.id, a]));
        if (!map.size) return;
        setItems((old) => old.map((a) => map.get(a.id) || a));
    }, []);
    const remove = useCallback((ids) => {
        const set = new Set(ids || []);
        if (!set.size) return;
        for (const id of set) seenIds.current.delete(id);
        setItems((old) => old.filter((a) => !set.has(a.id)));
    }, []);

    const pagesLoaded = Math.ceil(items.length / pageSize) + Math.floor(initialSkip / pageSize);
    return { items, loading, error, hasMore, autoMode, loadMore, sentinelRef, prepend, replace, remove, cursor, nextPage: pagesLoaded + 1 };
}
